import { AlertTriangle } from 'lucide-react';

import { compareDecimal, formatMoney } from '../../../../utils/decimalValue';
import RestaurantTable, {
  RestaurantTableColumn,
} from '../../shared/RestaurantTable';
import RestaurantTableActions from '../../shared/RestaurantTableActions';
import { InventoryItem } from '../types';

interface Props {
  items: InventoryItem[];
  onEdit: (item: InventoryItem) => void;
  onToggleActive: (item: InventoryItem) => void;
}

const formatStock = (value: number | null, unitCode: string) =>
  `${Number(value ?? 0).toLocaleString('es-SV', {
    maximumFractionDigits: 6,
  })} ${unitCode || ''}`.trim();

const InventoryTable = ({ items, onEdit, onToggleActive }: Props) => {
  const columns: RestaurantTableColumn<InventoryItem>[] = [
    {
      key: 'code',
      header: 'Código',
      className: 'whitespace-nowrap font-semibold text-primary',
      cell: (item) => item.rawMaterialCode,
    },
    {
      key: 'name',
      header: 'Materia prima',
      cell: (item) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-black dark:text-white">
            {item.rawMaterialName}
          </p>
          <p className="text-xs text-bodydark2">{item.unitName}</p>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Categoría',
      className: 'text-sm text-bodydark2',
      cell: (item) => item.categoryName,
    },
    {
      key: 'currentStock',
      header: 'Stock actual',
      className: 'whitespace-nowrap',
      cell: (item) => (
        <span
          className={`font-semibold ${
            compareDecimal(item.currentStock, 0) < 0
              ? 'text-danger'
              : item.lowStock
              ? 'text-warning'
              : 'text-black dark:text-white'
          }`}
        >
          {formatStock(item.currentStock, item.unitCode)}
        </span>
      ),
    },
    {
      key: 'minimumStock',
      header: 'Stock mínimo',
      className: 'whitespace-nowrap text-sm text-bodydark2',
      cell: (item) => formatStock(item.minimumStock, item.unitCode),
    },
    {
      key: 'maximumStock',
      header: 'Stock máximo',
      className: 'whitespace-nowrap text-sm text-bodydark2',
      cell: (item) =>
        item.maximumStock == null
          ? 'Sin límite'
          : formatStock(item.maximumStock, item.unitCode),
    },
    {
      key: 'averageCost',
      header: 'Costo promedio',
      className: 'whitespace-nowrap font-semibold text-black dark:text-white',
      cell: (item) => formatMoney(item.averageCost),
    },
    {
      key: 'lastPurchaseCost',
      header: 'Última compra',
      className: 'whitespace-nowrap text-sm text-bodydark2',
      cell: (item) => formatMoney(item.lastPurchaseCost),
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (item) =>
        item.lowStock && item.active ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning/15 px-2.5 py-1 text-xs font-semibold text-warning">
            <AlertTriangle className="h-3.5 w-3.5" /> Stock bajo
          </span>
        ) : (
          <span
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
              item.active
                ? 'border-success/20 bg-success/10 text-success'
                : 'border-danger/20 bg-danger/10 text-danger'
            }`}
          >
            {item.active ? 'Activo' : 'Inactivo'}
          </span>
        ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      className: 'text-right',
      headClassName: 'text-right',
      cell: (item) => (
        <RestaurantTableActions
          onEdit={() => onEdit(item)}
          onToggle={() => onToggleActive(item)}
          active={item.active}
          toggleLabel={{ on: 'Desactivar', off: 'Reactivar' }}
        />
      ),
    },
  ];

  return (
    <RestaurantTable
      columns={columns}
      rows={items}
      rowKey={(item) => item.id}
      rowClassName={(item) =>
        [
          item.active ? '' : 'opacity-65',
          item.lowStock && item.active ? 'bg-warning/5' : '',
        ]
          .filter(Boolean)
          .join(' ')
      }
      minWidth="min-w-[1200px]"
    />
  );
};

export default InventoryTable;
